<template>
  <AppShell>
    <section class="view on soa-view-overview">
      <div class="pagehead">
        <div>
          <h1>{{ t("ov_h") }}</h1>
          <p class="sub">{{ t("ov_sub") }}</p>
        </div>
        <button type="button" class="btn" @click="addBrand">{{ t("add_a_brand") }}</button>
      </div>

      <p v-if="loading" class="empty" role="status">{{ t("loading") }}</p>
      <p v-else-if="error" class="empty" role="alert">
        {{ error }} <button type="button" class="atbtn" @click="load">{{ t("retry") }}</button>
      </p>
      <div v-else-if="!brands.length" class="empty" role="status">
        <p>{{ t("ov_no_brands") }}</p>
        <router-link class="ov-add-link" :to="{ path: '/maya', query: { intent: 'add-brand' } }">{{ t("nav_add") }}</router-link>
      </div>
      <template v-else>
        <section v-if="overCapacity.length && inCapacity.length" class="brand-group" aria-labelledby="brands-in-capacity">
          <h2 id="brands-in-capacity">{{ t("brand_cap_in") }}</h2>
          <div class="brand-group-grid">
            <OverviewBrandCard v-for="brand in inCapacity" :key="brand.id" :brand="brand" :busy="retesting.has(brand.id)" @history="showHistory" @profile="editProfile" @retest="retest" />
          </div>
        </section>
        <section v-if="overCapacity.length" class="brand-group brand-group-over" aria-labelledby="brands-over-capacity">
          <h2 id="brands-over-capacity">{{ t("brand_cap_out") }}</h2>
          <p>{{ t("brand_cap_out_note") }}</p>
          <div class="brand-group-grid">
            <OverviewBrandCard v-for="brand in overCapacity" :key="brand.id" :brand="brand" over-capacity :busy="retesting.has(brand.id)" @upgrade="upgradeCapacity" @remove="promptRemove" />
          </div>
        </section>
        <div v-if="!overCapacity.length" class="grid">
          <OverviewBrandCard v-for="brand in inCapacity" :key="brand.id" :brand="brand" :busy="retesting.has(brand.id)" @history="showHistory" @profile="editProfile" @retest="retest" />
        </div>
      </template>
    </section>
    <dialog ref="removeDialog" class="brand-remove-dialog" aria-labelledby="brand-remove-title" aria-describedby="brand-remove-body" @cancel.prevent="closeRemoveDialog">
      <form @submit.prevent="confirmRemove">
        <button type="button" class="brand-remove-close" :aria-label="t('set_cancel')" @click="closeRemoveDialog">&times;</button>
        <h2 id="brand-remove-title">{{ removeDialogTitle }}</h2>
        <p id="brand-remove-body">{{ removeDialogBody }}</p>
        <p v-if="removeError" class="brand-remove-error" role="alert">{{ removeError }}</p>
        <div class="brand-remove-actions">
          <button type="submit" class="brand-remove-confirm" :disabled="removing">{{ removing ? t("loading") : t("brand_remove_confirm") }}</button>
          <button type="button" class="brand-remove-cancel" :disabled="removing" @click="closeRemoveDialog">{{ t("set_cancel") }}</button>
        </div>
      </form>
    </dialog>
  </AppShell>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import { removeBrand } from "../api/brands.js";
import OverviewBrandCard from "../components/OverviewBrandCard.vue";
import AppShell from "../layout/AppShell.vue";
import { soaToast } from "../legacy/soaToast.js";
import { brandState, loadBrands, selectBrand } from "../stores/brands";
import { useI18n } from "../useI18n";

const router = useRouter();
const { t } = useI18n();
const loading = ref(true), error = ref(""), retesting = ref(new Set()), removeDialog = ref(null), pendingRemoval = ref(null), removing = ref(false), removeError = ref("");
let controller, pollTimer;
let removeTrigger;
const brands = computed(() => brandState.items);
const inCapacity = computed(() => brands.value.filter((brand) => !isOverCapacity(brand)));
const overCapacity = computed(() => brands.value.filter((brand) => isOverCapacity(brand)));
const removeDialogTitle = computed(() => t("brand_remove_title").replace("{brand}", pendingRemoval.value?.brand || ""));
const removeDialogBody = computed(() => t("brand_remove_body").replace("{brand}", pendingRemoval.value?.brand || ""));

function isOverCapacity(brand) {
  return brand.evaluation_capacity_ok === false;
}
async function load({ silent = false } = {}) {
  controller?.abort();
  controller = new AbortController();
  if (!silent) loading.value = true;
  error.value = "";
  try {
    await loadBrands({ signal: controller.signal });
  } catch (caught) {
    if (caught.name !== "AbortError") error.value = caught.message || "Unable to load overview.";
  } finally {
    if (!silent) loading.value = false;
  }
}
function addBrand() { router.push({ path: "/maya", query: { intent: "add-brand" } }); }
function showHistory(brand) { selectBrand(brand.id); router.push({ path: "/reports", query: { brand_id: brand.id } }); }
function editProfile(brand) { selectBrand(brand.id); router.push("/profile"); }
function retest(brand) {
  if (retesting.value.has(brand.id) || brand.evaluation_pending) return;
  selectBrand(brand.id);
  router.push({ path: "/maya", query: { retest_brand: String(brand.id) } });
}
function upgradeCapacity() { router.push("/account/billing"); }
async function promptRemove(brand, event) {
  pendingRemoval.value = brand;
  removeError.value = "";
  removeTrigger = event?.currentTarget || document.activeElement;
  await nextTick();
  removeDialog.value?.showModal();
}
function closeRemoveDialog(force = false) {
  if (removing.value && !force) return;
  removeDialog.value?.close();
  pendingRemoval.value = null;
  removeError.value = "";
  requestAnimationFrame(() => {
    if (removeTrigger?.isConnected) removeTrigger.focus();
    else document.getElementById("main-content")?.focus();
    removeTrigger = null;
  });
}
async function confirmRemove() {
  const brand = pendingRemoval.value;
  if (!brand || removing.value) return;
  removing.value = true;
  removeError.value = "";
  try {
    const result = await removeBrand(brand.id);
    if (!result?.ok) throw new Error(t("brand_remove_error"));
    await loadBrands();
    closeRemoveDialog(true);
    soaToast(t("brand_removed"), "", "ok");
  } catch (caught) {
    removeError.value = caught.message || t("brand_remove_error");
  } finally {
    removing.value = false;
  }
}
function shouldPollBrands() {
  return brands.value.some((brand) => brand.evaluation_pending);
}
function onOverviewVisible() {
  if (document.visibilityState === "visible") load({ silent: true });
}
onMounted(() => {
  load();
  pollTimer = window.setInterval(() => {
    if (shouldPollBrands()) load({ silent: true });
  }, 5000);
  document.addEventListener("visibilitychange", onOverviewVisible);
});
onBeforeUnmount(() => {
  controller?.abort();
  if (pollTimer) window.clearInterval(pollTimer);
  document.removeEventListener("visibilitychange", onOverviewVisible);
});
</script>

<style scoped>
.ov-add-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 8px 4px;
  font-weight: 600;
  text-decoration: underline;
}
.ov-add-link:hover {
  text-decoration-thickness: 2px;
}
.brand-remove-dialog {
  position: fixed;
  inset: 0;
  margin: auto;
  width: min(420px, calc(100vw - 32px));
  height: fit-content;
  max-height: calc(100dvh - 32px);
  overflow: auto;
  border: 0;
  border-radius: 16px;
  padding: 0;
  color: #282D32;
  background: #FAF5F0;
  box-shadow: 0 24px 60px rgba(26, 35, 51, .28);
}
.brand-remove-dialog::backdrop { background: rgba(26, 35, 51, .42); }
.brand-remove-dialog form { position: relative; padding: 26px; }
.brand-remove-dialog h2 { margin: 0 34px 10px 0; font-size: 24px; color: #282D32; font-weight: 600; font-family: PingFangSC-Semibold, sans-serif; line-height: 1.35; }
.brand-remove-dialog p { margin: 0; font-family: PingFangSC-Regular, sans-serif; font-size: 18px; color: #282D32; font-weight: 400; line-height: 1.5; }
.brand-remove-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--mute);
  cursor: pointer;
  font: inherit;
  font-size: 24px;
  line-height: 1;
}
.brand-remove-close:hover { background: var(--bad-fill); color: var(--verm); }
.brand-remove-error { margin-top: 12px !important; color: var(--verm) !important; font-size: var(--fs-footnote) !important; }
.brand-remove-actions { display: flex; justify-content: center; gap: 12px; margin-top: 22px; }
.brand-remove-actions button { flex: 1; min-width: 0; min-height: 44px; border-radius: 16px; padding: 9px 16px; background: #FFFFFF; font-family: PingFangSC-Semibold, sans-serif; font-size: 16px; font-weight: 600; letter-spacing: 0; text-align: center; cursor: pointer; }
.brand-remove-confirm { border: 1px solid #282D32; color: #282D32; }
.brand-remove-cancel { border: 1px solid #FF5A00; color: #FF5A00; }
.brand-remove-confirm:hover { background: #F0EAE5; }
.brand-remove-cancel:hover { background: #FFF1E8; }
.brand-remove-actions button:disabled { cursor: wait; opacity: .6; }
@media (max-width: 560px) {
  .soa-view-overview .grid,
  .soa-view-overview .brand-group-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
